import './style.css';
import { router } from './router.js';
import { applyMeta } from './seo.js';
import { mountShell } from './components/shell.js';
import { createPlayground } from './components/playground.js';
import { createCaseStudy } from './components/home-v2.js';
import { createLanding } from './components/landing.js';
import { createAbout } from './components/about.js';
import { createContact } from './components/contact.js';
import solaceData from './case-studies/solace.js';
import atlasData from './case-studies/atlas.js';
import paiwaresData from './case-studies/paiwares.js';
import santosData from './case-studies/santos.js';

const app = document.getElementById('app');

const BASE_TITLE = 'Devonte Ezell — UX Designer';
const setTitle = (suffix) => {
  document.title = suffix ? `${suffix} · Devonte Ezell` : BASE_TITLE;
};

/* ── Mounted-state cache ──────────────────────────────────────────
   We track which "world" (landing | solace) is currently in `app` and
   which inner instance owns the foreground. Each route handler asks
   the helpers below to ensure its world is set up before rendering.
   This keeps the swap-on-mode-change logic in one place while letting
   each route own its own content. */

let currentWorld = null;            // 'landing' | 'about' | 'solace'
let currentShellKey = null;          // identifies the active sidebar config
let landingInstance = null;          // { element, destroy } from createLanding
let aboutInstance = null;            // { element, destroy } from createAbout
let contactInstance = null;          // { element, destroy } from createContact
let solaceView = null;               // inner view returned by mountShell
let activeSection = null;            // current cached section component (for /solace/:slug/:step)

/* Tear down whatever is in the app and reset world-specific caches. */
function teardown() {
  if (landingInstance) {
    landingInstance.destroy();
    landingInstance = null;
  }
  if (aboutInstance) {
    aboutInstance.destroy();
    aboutInstance = null;
  }
  if (contactInstance) {
    contactInstance.destroy();
    contactInstance = null;
  }
  // Solace shell uses no animation/listeners we need to abort here —
  // its contents are managed via solaceView.innerHTML. Just drop refs.
  solaceView = null;
  activeSection = null;
  currentShellKey = null;
  app.innerHTML = '';
  currentWorld = null;
}

/* Ensure the case-study shell is mounted with the given sidebar config
   and return its inner view element. Rebuilds the shell when switching
   between case studies (different sidebar metadata). */
function ensureCaseStudyShell(sidebar) {
  const key = sidebar?.wordmark || 'default';
  if (currentWorld !== 'solace' || currentShellKey !== key) {
    teardown();
    solaceView = mountShell(app, { sidebar });
    currentWorld = 'solace';
    currentShellKey = key;
  }
  return solaceView;
}

/* Mount the landing component. Always rebuilds a fresh instance to make
   the typewriter and Three.js scene start from their initial state on
   every visit. */
function mountLanding() {
  teardown();
  landingInstance = createLanding();
  app.appendChild(landingInstance.element);
  currentWorld = 'landing';
}

/* Mount the about page. Owns its own Three.js scene, so we keep the
   instance handle around for `teardown()` to dispose. */
function mountAbout() {
  teardown();
  aboutInstance = createAbout();
  app.appendChild(aboutInstance.element);
  currentWorld = 'about';
}

/* Mount the contact page. */
function mountContact() {
  teardown();
  contactInstance = createContact();
  app.appendChild(contactInstance.element);
  currentWorld = 'contact';
}

router
  // Home is reachable at both "/" (canonical) and "/home".
  .add('/', () => {
    mountLanding();
    applyMeta('home');
  })
  .add('/home', () => {
    mountLanding();
    applyMeta('home');
  })
  .add('/about', () => {
    mountAbout();
    applyMeta('about');
  })
  .add('/contact', () => {
    mountContact();
    applyMeta('contact');
  })
  // Legacy URLs redirect to the canonical Solace case-study path.
  .add('/solace', () => router.go('/solace-v2'))
  .add('/solace/:slug/:step', () => router.go('/solace-v2'))
  .add('/solace-v2', () => {
    const view = ensureCaseStudyShell(solaceData.sidebar);
    view.innerHTML = '';
    activeSection = null;
    view.appendChild(createCaseStudy(solaceData));
    applyMeta('solace-v2');
  })
  .add('/atlas', () => {
    const view = ensureCaseStudyShell(atlasData.sidebar);
    view.innerHTML = '';
    activeSection = null;
    view.appendChild(createCaseStudy(atlasData));
    applyMeta('atlas');
  })
  .add('/paiwares', () => {
    const view = ensureCaseStudyShell(paiwaresData.sidebar);
    view.innerHTML = '';
    activeSection = null;
    view.appendChild(createCaseStudy(paiwaresData));
    applyMeta('paiwares');
  })
  .add('/santos', () => {
    const view = ensureCaseStudyShell(santosData.sidebar);
    view.innerHTML = '';
    activeSection = null;
    view.appendChild(createCaseStudy(santosData));
    applyMeta('santos');
  })
  .add('/dev', () => {
    const view = ensureCaseStudyShell(solaceData.sidebar);
    view.innerHTML = '';
    activeSection = null;
    view.appendChild(createPlayground());
    setTitle('Component playground');
  })
  .add('*', () => {
    // Any other unmatched path → land on the home page.
    router.go('/home');
  })
  .start();

/* ── Global keyboard: Esc returns from /about → /home. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (document.body.classList.contains('mobile-nav-open')) return;
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  const path = window.location.pathname || '/';

  if (path === '/about') {
    router.go('/home');
  }
});
